import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Building2, ChevronRight, Filter, RotateCcw, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getSmartAnalyticsCompanies } from "../../Services/SmartAnalyticsService";
import {
  COMPANY_BOOLEAN_FILTER_OPTIONS,
  COMPANY_PLAN_STATUS_OPTIONS,
  SmartAnalyticsCompanyFilters,
  SmartAnalyticsCompanyRow,
} from "../../Types/SmartAnalyticsType";
import Pagination from "../../../Shared/Pagination/Pagination";
import { PaginationResponse } from "../../../Shared/Pagination/PaginationResponse";
import { formatDateTime } from "../../../Shared/Utils/HelperUtils";
import {
  EmptyState,
  fieldClass,
  LoadingRows,
  MiniStat,
  PAGE_SIZE,
  planBadge,
  SectionCard,
} from "./SmartAnalyticsShared";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);

const SmartAnalyticsRecruitersTab = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<PaginationResponse<SmartAnalyticsCompanyRow> | null>(null);
  const [filters, setFilters] = useState<SmartAnalyticsCompanyFilters>({
    search: "",
    planStatus: "",
    hasRecruiters: "",
    hasPayments: "",
  });
  const [appliedFilters, setAppliedFilters] = useState<SmartAnalyticsCompanyFilters>(filters);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  const hasFilters = useMemo(
    () => Object.values(appliedFilters).some((value) => value !== ""),
    [appliedFilters]
  );

  const load = useCallback(async () => {
    const response = await getSmartAnalyticsCompanies(appliedFilters, page, PAGE_SIZE);
    if (response.success) {
      setCompanies(response.data);
    }
  }, [appliedFilters, page]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const applyFilters = () => {
    setAppliedFilters(filters);
    setPage(0);
  };

  const resetFilters = () => {
    const empty = { search: "", planStatus: "", hasRecruiters: "", hasPayments: "" };
    setFilters(empty);
    setAppliedFilters(empty);
    setPage(0);
  };

  const updateFilter = (key: keyof SmartAnalyticsCompanyFilters, value: string) => {
    setFilters((previous) => ({ ...previous, [key]: value }));
  };

  const totalCompanies = companies?.totalElements ?? 0;
  const activePlans = companies?.content.filter((c) => c.planActive).length ?? 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <MiniStat
          label="Companies"
          value={totalCompanies}
          sub="Registered organizations"
          icon={<Building2 className="h-4 w-4" />}
          tone="cyan"
        />
        <MiniStat
          label="Active plans"
          value={activePlans}
          sub="Currently paid or trial"
          icon={<Building2 className="h-4 w-4" />}
          tone="emerald"
        />
        <MiniStat
          label="Total recruiters"
          value={companies?.content.reduce((sum, c) => sum + c.recruiterCount, 0) ?? 0}
          sub="Across listed companies"
          icon={<Building2 className="h-4 w-4" />}
        />
        <MiniStat
          label="Total revenue"
          value={formatCurrency(companies?.content.reduce((sum, c) => sum + c.totalRevenue, 0) ?? 0)}
          sub="From listed companies"
          icon={<Building2 className="h-4 w-4" />}
          tone="amber"
        />
      </div>

      <SectionCard
        title={
          <div>
            <h2 className="flex items-center gap-2 text-sm font-black text-slate-100">
              <Building2 className="h-4 w-4 text-cyan-400" />
              Companies / Organizations
            </h2>
            <p className="mt-1 text-[10px] font-semibold text-slate-500">
              Sorted by last activity. Click a row to open the full company dashboard.
            </p>
          </div>
        }
        action={
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-600" />
              <input
                value={filters.search}
                onChange={(event) => updateFilter("search", event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && applyFilters()}
                placeholder="Search company, domain, admin"
                className={`${fieldClass} w-56 pl-9`}
              />
            </div>
            <select
              value={filters.planStatus}
              onChange={(event) => updateFilter("planStatus", event.target.value)}
              className={fieldClass}
            >
              {COMPANY_PLAN_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              value={filters.hasRecruiters}
              onChange={(event) => updateFilter("hasRecruiters", event.target.value)}
              className={fieldClass}
              title="Has recruiters"
            >
              {COMPANY_BOOLEAN_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  Recruiters: {option.label}
                </option>
              ))}
            </select>
            <select
              value={filters.hasPayments}
              onChange={(event) => updateFilter("hasPayments", event.target.value)}
              className={fieldClass}
              title="Has payments"
            >
              {COMPANY_BOOLEAN_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  Payments: {option.label}
                </option>
              ))}
            </select>
            {hasFilters && (
              <button
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2 text-xs font-semibold text-slate-400 transition hover:text-slate-100"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reset
              </button>
            )}
            <button
              onClick={applyFilters}
              className="inline-flex items-center gap-1.5 rounded-xl bg-cyan-500/10 px-4 py-2 text-xs font-black uppercase tracking-wider text-cyan-400 transition hover:bg-cyan-500/20"
            >
              <Filter className="h-3.5 w-3.5" />
              Apply
            </button>
          </div>
        }
      >
        <table className="w-full">
          <thead className="border-y border-slate-800 bg-slate-900/60">
            <tr>
              <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">
                Company
              </th>
              <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">
                Company admin
              </th>
              <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">
                Plan
              </th>
              <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">
                Recruiters
              </th>
              <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">
                Jobs / Invites
              </th>
              <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">
                Payments
              </th>
              <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">
                Last activity
              </th>
              <th className="px-5 py-4 text-right text-[10px] font-black uppercase tracking-widest text-slate-500">
                Open
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <LoadingRows rows={4} cols={8} />
            ) : !companies || companies.content.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <EmptyState
                    icon={<Building2 className="h-6 w-6" />}
                    title="No companies match"
                    body="Try a different search term or check back once recruiters have started using the platform."
                  />
                </td>
              </tr>
            ) : (
              companies.content.map((company) => (
                <tr
                  key={company.companyId}
                  onClick={() => navigate(`/admin/smart-analytics/company/${company.companyId}`)}
                  className="cursor-pointer border-t border-slate-800/70 transition hover:bg-slate-900/40"
                >
                  <td className="px-5 py-4">
                    <p className="text-xs font-bold text-slate-200">{company.companyName}</p>
                    <p className="text-[10px] font-semibold text-slate-500">
                      {company.companyDomain || company.industry || "-"}
                    </p>
                    {company.companySizeRange && (
                      <p className="text-[10px] font-semibold text-slate-600">{company.companySizeRange}</p>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-xs font-bold text-slate-200">
                      {company.adminName || "-"}
                    </p>
                    <p className="text-[10px] font-semibold text-slate-500">{company.adminEmail || "-"}</p>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex flex-col items-start gap-1.5">
                      {planBadge(company.planName, !!company.planActive, !company.planActive)}
                      {company.planExpireDate && (
                        <span className="text-[10px] font-semibold text-slate-500">
                          Expires {formatDateTime(company.planExpireDate)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-xs font-black text-slate-200">
                      {company.activeRecruiterCount}
                      <span className="text-[10px] font-semibold text-slate-500"> / {company.recruiterCount}</span>
                    </p>
                    <p className="text-[10px] font-semibold text-slate-500">active</p>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-xs font-semibold text-slate-300">
                      {company.jobTemplateCount} jobs
                    </p>
                    <p className="text-[10px] font-semibold text-slate-500">
                      {company.acceptedInvitationCount} accepted / {company.invitationCount} invites
                    </p>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-xs font-black text-emerald-400">
                      {formatCurrency(company.totalRevenue)}
                    </p>
                    <p className="text-[10px] font-semibold text-slate-500">
                      {company.paymentCount} payments
                    </p>
                  </td>
                  <td className="px-5 py-4 text-xs font-semibold text-slate-500">
                    {formatDateTime(company.lastActivityAt ?? undefined)}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <ChevronRight className="inline h-4 w-4 text-slate-500" />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {companies && companies.totalPages > 1 && (
          <Pagination {...companies} setPage={setPage} />
        )}
      </SectionCard>
    </div>
  );
};

export default SmartAnalyticsRecruitersTab;
